import React, { useEffect } from 'react';
import { useAuth, isTeamMemberRole } from './AuthContext';
import { vendorApi } from '../store/vendorApi';
import { teamMemberApi } from '../store/teamMemberApi';

/**
 * Syncs RTK Query /me results to AuthContext. Single source of truth for user data fetch.
 */
export const AuthDataSync: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { firebaseUser, role, setUser, setAuthError } = useAuth();
  const isVendor = role === 'vendor';
  const isTeamMember = isTeamMemberRole(role ?? undefined);

  const { data: vendorData, isError: isVendorError } = vendorApi.useGetVendorMeQuery(undefined, {
    skip: !firebaseUser || !isVendor,
  });
  const { data: teamData, isError: isTeamError } = teamMemberApi.useGetMyProfileQuery(undefined, {
    skip: !firebaseUser || !isTeamMember,
  });

  useEffect(() => {
    if (!firebaseUser || !role) return;
    if (isVendor && vendorData) {
      setUser({
        id: vendorData.id,
        username: vendorData.email ?? '',
        name: `${vendorData.firstName} ${vendorData.lastName}`,
        avatar: vendorData.avatar ?? undefined,
        role: 'vendor',
        vendor: vendorData,
      });
    } else if (isTeamMember && teamData) {
      setUser({
        id: teamData.id,
        username: teamData.email,
        name: teamData.name,
        avatar: teamData.avatar,
        role,
        teamMember: teamData,
      });
    } else if ((isVendor && isVendorError) || (isTeamMember && isTeamError)) {
      // The profile fetch that resolves the session failed outright (network
      // error, unexpected 403/500, etc). Without this, screens that wait on
      // isAuthenticated — e.g. the trial signup spinner — would wait forever
      // for a session that's never coming.
      setAuthError('We could not load your account. Please try again.');
    }
  }, [firebaseUser, role, isVendor, isTeamMember, vendorData, teamData, isVendorError, isTeamError, setUser, setAuthError]);

  return <>{children}</>;
};
